import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { cn } from "@/lib/utils";
import type { AnswerRecord } from "@/types/assessment";
import type {
  Attempt,
  ConnectedBookResponse,
  GapAnalysisDTO,
} from "@/types/api";
import { useAuthStore } from "@/lib/stores/auth";
import { isFreeUser as isFreeUserHelper } from "@/lib/user-type";
import { GapAnalysisReport } from "./gap-analysis-report";
import { GapAnalysisRetryPanel } from "./gap-analysis-retry-panel";
import {
  TrophyIcon,
  HomeIcon,
  CheckCircle2Icon,
  XCircleIcon,
  SparklesIcon,
  ArrowRightIcon,
} from "lucide-react";

interface AssessmentResultsProps {
  title: string;
  score: number;
  totalQuestions: number;
  submittedAnswers: Record<number, AnswerRecord>;
  onBackToDashboard: () => void;
  blindMode?: boolean;
  // Populated for completed blind attempts. When `gapAnalysis` is undefined
  // and `attempt` is set, the user sees the retry panel; when both are set,
  // the GapAnalysisReport renders.
  gapAnalysis?: GapAnalysisDTO;
  attempt?: Attempt;
  connectedBook?: ConnectedBookResponse | null;
}

export function AssessmentResults({
  title,
  score,
  totalQuestions,
  submittedAnswers,
  onBackToDashboard,
  blindMode = false,
  gapAnalysis,
  attempt,
  connectedBook,
}: AssessmentResultsProps) {
  const user = useAuthStore((s) => s.user);
  const isFreeUser = isFreeUserHelper(user);

  // Blind-mode variant: gap-analysis report (when generated) or retry panel
  // (when LLM hasn't produced one yet). Sits directly on the page background
  // — no Card wrapper — per the design's "sit on cream, not in a card" rule.
  if (blindMode) {
    if (gapAnalysis && attempt) {
      return (
        <GapAnalysisReport
          gapAnalysis={gapAnalysis}
          assessmentTitle={title}
          answeredCount={Object.keys(submittedAnswers).length}
          connectedBook={connectedBook ?? null}
          user={user}
        />
      );
    }
    if (attempt) {
      return <GapAnalysisRetryPanel attemptId={attempt.id} />;
    }
    // Defensive fallback when called for a blind attempt before the
    // attempt object has loaded. Keeps the previous stub for that brief window.
    return (
      <Card className="shadow-sm">
        <CardHeader className="text-center pb-2">
          <div className="flex justify-center mb-4">
            <div className="size-16 rounded-full flex items-center justify-center bg-primary/10">
              <SparklesIcon className="size-8 text-primary" />
            </div>
          </div>
          <CardTitle className="text-2xl">Assessment Complete</CardTitle>
          <p className="text-muted-foreground mt-2">{title}</p>
        </CardHeader>
        <CardContent className="space-y-6">
          <p className="text-center text-muted-foreground">
            Your responses have been recorded.
          </p>
          <div className="flex justify-center pt-2">
            <Button onClick={onBackToDashboard} size="lg">
              <HomeIcon className="size-4" />
              Back to Dashboard
            </Button>
          </div>
        </CardContent>
      </Card>
    );
  }

  // Calculate stats
  const answers = Object.values(submittedAnswers);
  const correctCount = answers.filter((a) => a.feedback?.isCorrect).length;
  const answeredCount = answers.length;
  const percentage = totalQuestions > 0 ? Math.round((score / totalQuestions) * 100) : 0;

  // Determine performance level
  const performanceLevel =
    percentage >= 80
      ? "excellent"
      : percentage >= 60
        ? "good"
        : percentage >= 40
          ? "fair"
          : "needs-improvement";

  return (
    <Card className="shadow-sm">
      <CardHeader className="text-center pb-2">
        <div className="flex justify-center mb-4">
          <div
            className={cn(
              "size-16 rounded-full flex items-center justify-center",
              performanceLevel === "excellent" && "bg-green-100 dark:bg-green-900/30",
              performanceLevel === "good" && "bg-blue-100 dark:bg-blue-900/30",
              performanceLevel === "fair" && "bg-amber-100 dark:bg-amber-900/30",
              performanceLevel === "needs-improvement" && "bg-red-100 dark:bg-red-900/30"
            )}
          >
            <TrophyIcon
              className={cn(
                "size-8",
                performanceLevel === "excellent" && "text-green-600",
                performanceLevel === "good" && "text-blue-600",
                performanceLevel === "fair" && "text-amber-600",
                performanceLevel === "needs-improvement" && "text-red-600"
              )}
            />
          </div>
        </div>
        <CardTitle className="text-2xl">Assessment Complete!</CardTitle>
        <p className="text-muted-foreground mt-2">{title}</p>
      </CardHeader>

      <CardContent className="space-y-6">
        {/* Score display */}
        <div className="text-center space-y-2">
          <div
            className={cn(
              "text-4xl font-bold",
              performanceLevel === "excellent" && "text-green-600",
              performanceLevel === "good" && "text-blue-600",
              performanceLevel === "fair" && "text-amber-600",
              performanceLevel === "needs-improvement" && "text-red-600"
            )}
          >
            {percentage}%
          </div>
          <p className="text-sm text-muted-foreground">
            {score} out of {totalQuestions} points
          </p>
        </div>

        {/* Stats */}
        <div className="grid grid-cols-2 gap-4">
          <div className="text-center p-4 bg-muted/50 rounded-lg">
            <div className="flex items-center justify-center gap-2 mb-1">
              <CheckCircle2Icon className="size-4 text-green-600" />
              <span className="text-2xl font-semibold">{correctCount}</span>
            </div>
            <p className="text-xs text-muted-foreground">Correct</p>
          </div>
          <div className="text-center p-4 bg-muted/50 rounded-lg">
            <div className="flex items-center justify-center gap-2 mb-1">
              <XCircleIcon className="size-4 text-red-600" />
              <span className="text-2xl font-semibold">
                {answeredCount - correctCount}
              </span>
            </div>
            <p className="text-xs text-muted-foreground">Incorrect</p>
          </div>
        </div>

        {/* Performance message */}
        <div
          className={cn(
            "p-4 rounded-lg text-center",
            performanceLevel === "excellent" && "bg-green-50 dark:bg-green-950/30",
            performanceLevel === "good" && "bg-blue-50 dark:bg-blue-950/30",
            performanceLevel === "fair" && "bg-amber-50 dark:bg-amber-950/30",
            performanceLevel === "needs-improvement" && "bg-red-50 dark:bg-red-950/30"
          )}
        >
          <p
            className={cn(
              "font-medium",
              performanceLevel === "excellent" && "text-green-700 dark:text-green-400",
              performanceLevel === "good" && "text-blue-700 dark:text-blue-400",
              performanceLevel === "fair" && "text-amber-700 dark:text-amber-400",
              performanceLevel === "needs-improvement" && "text-red-700 dark:text-red-400"
            )}
          >
            {performanceLevel === "excellent" && "Excellent work! You've mastered this material."}
            {performanceLevel === "good" && "Good job! You have a solid understanding."}
            {performanceLevel === "fair" && "Not bad! Consider reviewing the topics you missed."}
            {performanceLevel === "needs-improvement" &&
              "Keep practicing! Review the material and try again."}
          </p>
        </div>

        {/* CTA for free users */}
        {isFreeUser && (
          <div className="p-4 rounded-lg bg-gradient-to-br from-primary/5 to-primary/10 border border-primary/20">
            <div className="text-center space-y-3">
              <p className="font-medium">
                Close Your Knowledge Gaps
              </p>
              <Button asChild size="lg" className="gap-2">
                <a
                  href="https://irreplaceable.engineer/lab/"
                  target="_blank"
                  rel="noopener noreferrer"
                >
                  <SparklesIcon className="size-4" />
                  Join the Lab
                  <ArrowRightIcon className="size-4" />
                </a>
              </Button>
            </div>
          </div>
        )}

        {/* Actions */}
        <div className="flex justify-center pt-2">
          <Button onClick={onBackToDashboard} size="lg" variant={isFreeUser ? "outline" : "default"}>
            <HomeIcon className="size-4" />
            Back to Dashboard
          </Button>
        </div>
      </CardContent>
    </Card>
  );
}
